/** Drift of the plate readings between repetition 1 and repetition 2 of the
 *  repeatability test, per capture mode. A cell's mode reading carries the
 *  L*a*b* of every repetition in ``lab_copies`` (index 0 = repetition 1). */

/** ``[repetition 1, repetition 2]`` L*a*b* of a cell in one mode; null when
 *  the reading has no per-repetition values. */
export function repetitionPair(cell, mode) {
    const copies = cell?.modes?.[String(mode)]?.lab_copies;
    return Array.isArray(copies) && copies.length >= 2 ? [copies[0], copies[1]] : null;
}

/** Mean and sample standard deviation (0 for a single value). */
function meanSd(values) {
    const mean = values.reduce((sum, v) => sum + v, 0) / values.length;
    const squares = values.reduce((sum, v) => sum + (v - mean) ** 2, 0);
    return { mean, sd: values.length > 1 ? Math.sqrt(squares / (values.length - 1)) : 0 };
}

/**
 * Per capture mode: ΔL*, Δa*, Δb* (repetition 2 − repetition 1) as mean ±
 * standard deviation, and the mean / max ΔE00 between the repetitions, over
 * the retained patches (every measured patch with ``includeExcluded``).
 * The ΔE00 is the backend's ``deltaE`` (worst repetition against
 * repetition 1, i.e. repetition 1 vs 2 with two repetitions). Modes without
 * a patch to show are left out.
 */
export function repetitionDriftSummary(cells, modes, includeExcluded = false) {
    return Array.from({ length: modes }, (_, i) => i + 1).map((mode) => {
        const pairs = cells
            .filter((cell) => includeExcluded || cell.kept)
            .map((cell) => ({ cell, pair: repetitionPair(cell, mode) }))
            .filter(({ pair }) => pair);
        if (!pairs.length) return null;
        const difference = (channel) => pairs.map(({ pair }) => pair[1][channel] - pair[0][channel]);
        const deltaE = pairs.map(({ cell }) => cell.modes[String(mode)].deltaE);
        return {
            mode,
            count: pairs.length,
            dL: meanSd(difference(0)),
            da: meanSd(difference(1)),
            db: meanSd(difference(2)),
            deltaE: { mean: deltaE.reduce((sum, v) => sum + v, 0) / deltaE.length, max: Math.max(...deltaE) },
        };
    }).filter(Boolean);
}

/** A signed difference to two decimals: "+0.84", "−0.12", "0.00". */
export function signedDifference(value) {
    const rounded = Number(value.toFixed(2));
    if (rounded === 0) return "0.00";
    return `${rounded > 0 ? "+" : "−"}${Math.abs(rounded).toFixed(2)}`;
}
